import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CoreModule } from '@core/core.module';
import { EnvelopeService } from '@services/envelope/envelope.service';
import { SendEnvelopeRequest } from '@models/e-signing';
import { BUTTON_PRESETS } from '@ui/button/button.component';
import { MODAL_DATA } from '@ui/modal/modal-data.token';
import { ModalService } from '@ui/modal/modal.service';
import { UiModule } from '@ui/ui.module';
import { AVATAR_VARIANT_ORDER } from '@ui/utils/constants';
import { AtomicModule } from 'src/app/shared/atomic/atomic.module';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import { STATUSES } from '@models/statuses';
import { Logger } from 'src/app/shared/core/logger';
import { CoreButtonSecondaryFillComponent } from '@core/ui';
import { BusinessState } from 'src/app/shared/state/business/business.state';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { select } from '@ngxs/store';
import { BusinessUsersState } from 'src/app/shared/state/business-users/business-users.state';

interface ESigningSendEnvelopeModalData {
	matterId: string;
	envelopeId: string;
}
@Component({
	selector: 'app-e-signing-send-envelope-modal',
	imports: [
		UiModule,
		CoreModule,
		AtomicModule,
		FormsModule,
		IconsModule,
		CoreButtonSecondaryFillComponent,
	],
	templateUrl: './e-signing-send-envelope-modal.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ESigningSendEnvelopeModalComponent implements OnInit {
	private MODAL_DATA = inject<ESigningSendEnvelopeModalData>(MODAL_DATA);
	private modalService = inject(ModalService);
	private envelopeService = inject(EnvelopeService);
	private defaultNote = select(BusinessState.getDefaultNote);
	public isLoggedInUserAdmin = select(BusinessUsersState.isLoggedInUserAdmin);

	public BUTTON_PRESETS = BUTTON_PRESETS;
	public variantOrderList = AVATAR_VARIANT_ORDER;
	public recipients_ = this.envelopeService.getParticipatingClientsByEnvelopeId(
		this.MODAL_DATA.envelopeId,
	);

	public envelope_ = computed(() =>
		this.envelopeService
			.envelopeConfigs_()
			.find((env) => env.id === this.MODAL_DATA.envelopeId),
	);
	public modalStatus_ = signal<STATUSES | null>(null);

	public note = signal(this.defaultNote());

	constructor() {
		effect(() => {
			if (!this.envelope_()?.note) {
				this.note.set(this.defaultNote());
			}
		});
	}
	ngOnInit() {
		const envelope = this.envelope_();

		if (envelope?.note) {
			this.note.set(envelope.note);
		}

		this.modalService.setPrimaryAction('Send Now', async () => {
			try {
				this.modalStatus_.set(STATUSES.SENDING);
				this.modalService.clearPrimaryAction();
				this.modalService.clearSecondaryAction();

				const request: SendEnvelopeRequest = {
					note: this.note(),
					isSignatureDrawOnly: envelope?.isSignatureDrawOnly ?? false,
				};
				await this.envelopeService.sendEnvelope(
					this.MODAL_DATA.matterId,
					this.MODAL_DATA.envelopeId,
					request,
				);

				this.modalStatus_.set(STATUSES.SENT_ALT);
				await new Promise((res) => setTimeout(res, 500));
			} catch (error) {
				Logger.exception(error, { msg: 'Error sending envelope' });
			} finally {
				this.modalService.closeModal();
			}
		});
	}

	goToESigningSettings() {
		const url = new URL(
			UrlService.Manage_Finances_General(),
			window.location.origin,
		);

		url.hash = 'E_SIGNING';
		window.open(url.toString(), '_blank', 'noopener,noreferrer');
	}

	onNoteDebounced(value: string) {
		this.envelopeService
			.updateNotes(this.MODAL_DATA.envelopeId, value)
			.catch((error) => {
				Logger.error(`Failed to update note: ${error}`);
			});
	}

	public editRecipients = () => {
		this.envelopeService.openEditRecipientModal(
			this.MODAL_DATA.envelopeId,
			false,
			() =>
				this.envelopeService.openSendEnvelopeModal(
					this.MODAL_DATA.matterId,
					this.MODAL_DATA.envelopeId,
				),
		);
	};
}
